/* Global koi cart tracking sync.
 *
 * Keeps the server-side cart-tracking rows (which drive the "In N carts"
 * competition badges) in step with the real cart no matter where a change
 * happens — cart drawer, product page, collection quick-toggle. Without
 * this, a koi removed outside the cart page kept its row for up to 24h, so
 * the remover still counted as competition.
 *
 * On every cartUpdate pub/sub event: re-fetch /cart.js (event payload shapes
 * vary by source — add.js responses only carry the added items), collect the
 * tracked variant ids (koi + Dealer Select), and post an authoritative
 * cart-sync. The server prunes this visitor's rows not in the list; an empty
 * list clears them all.
 *
 * The result is broadcast as a `ck:cart-synced` document event carrying the
 * tracked variant ids and the counts the server returned for them, so the
 * product-page badge (product-in-carts.js) repaints from this one read and
 * one sync instead of running its own /cart.js fetch and cart-add/remove
 * round trip on the same cart event.
 *
 * The cart page is excluded: cart-in-carts.js already runs the same
 * authoritative sync there (driven by DOM observers) and also paints badges.
 */
(() => {
  let syncTimer = null;

  // Any live fish, koi or Tamasaba — see assets/ck-fish.js.
  const isTrackedType = (type) => CKFish.isFish(type);

  function getVisitorId(createIfMissing) {
    const key = 'champkoi_visitor_id';
    let id = localStorage.getItem(key);
    if (!id && createIfMissing) {
      id = crypto.randomUUID();
      localStorage.setItem(key, id);
    }
    return id;
  }

  async function syncFromCart() {
    try {
      const res = await fetch(`/cart.js?_ck=${Date.now()}`, {
        credentials: 'same-origin',
        cache: 'no-store',
      });
      const cart = await res.json();
      const variantIds = [
        ...new Set(
          (cart.items || [])
            .filter((item) => isTrackedType(item.product_type))
            .map((item) => String(item.variant_id))
        ),
      ];

      // Don't mint a visitor id just to clear rows that can't exist yet.
      const visitorId = getVisitorId(variantIds.length > 0);
      let counts = {};
      if (visitorId) {
        const proxyRes = await fetch('/apps/champkoi', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'cart-sync',
            variant_ids: variantIds,
            visitor_id: visitorId,
          }),
        });
        const data = await proxyRes.json().catch(() => null);
        if (data && data.success && data.counts) counts = data.counts;
      }

      document.dispatchEvent(new CustomEvent('ck:cart-synced', {
        detail: { variantIds, counts },
      }));
    } catch {
      // Non-critical — the 24h staleness cutoff remains the backstop
    }
  }

  function debouncedSync() {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(syncFromCart, 300);
  }

  function init() {
    // Cart page: cart-in-carts.js owns the authoritative sync there.
    if (document.getElementById('main-cart-items')) return;

    document.addEventListener('cart:refresh', debouncedSync);
    // Bare identifiers — pubsub.js top-level consts don't attach to window.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, debouncedSync);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
